import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  GESTION_ETAPA_KEYS,
  GESTION_ETAPA_LABELS,
  PERMISOS_CAMPOS_POR_ETAPA,
  type GestionEtapaKey,
  parsePermisosCamposJson,
} from "../../config/proyectosPermisosCatalog";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const getUsuarioCreador = () => {
  const u = localStorage.getItem("user");
  return u ? atob(u) : "";
};

type UsuarioLista = {
  id: number;
  nombre: string;
  usuario: string;
  rol: { nombre: string };
};

export default function PermisosProyectosPanel() {
  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getToken(), []);

  const [loadingLista, setLoadingLista] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [usuarios, setUsuarios] = React.useState<UsuarioLista[]>([]);
  const [busqueda, setBusqueda] = React.useState("");

  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [loadingDetalle, setLoadingDetalle] = React.useState(false);
  const [detalle, setDetalle] = React.useState<any>(null);
  const [etapaTab, setEtapaTab] = React.useState(0);

  const [puedeCrear, setPuedeCrear] = React.useState(false);
  const [puedeVerIndex, setPuedeVerIndex] = React.useState(false);
  const [puedeGestionar, setPuedeGestionar] = React.useState(false);
  const [puedeEditar, setPuedeEditar] = React.useState(false);
  const [puedeEliminar, setPuedeEliminar] = React.useState(false);
  const [camposPorEtapa, setCamposPorEtapa] = React.useState(() => parsePermisosCamposJson("{}"));

  const [guardando, setGuardando] = React.useState(false);
  const [okMsg, setOkMsg] = React.useState<string | null>(null);

  const cargarLista = React.useCallback(async () => {
    try {
      setLoadingLista(true);
      setError(null);
      const res = await fetch(`${baseUrl}/usuarios/obtenerUsuariosProyectos`, {
        headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar usuarios del módulo proyectos.");
      setUsuarios(await res.json());
    } catch (e: any) {
      setError(e.message || "Error al cargar usuarios.");
    } finally {
      setLoadingLista(false);
    }
  }, [baseUrl, token]);

  React.useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  const etapaActual: GestionEtapaKey = GESTION_ETAPA_KEYS[etapaTab] ?? "solicitud_servicio";

  const cargarDetalle = async (id: number) => {
    try {
      setLoadingDetalle(true);
      setError(null);
      setOkMsg(null);
      const res = await fetch(`${baseUrl}/usuarios/${id}`, {
        headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar el usuario.");
      const data = await res.json();
      setDetalle(data);
      const p = data?.permisos;
      setPuedeCrear(!!p?.puede_crear);
      setPuedeVerIndex(!!p?.puede_ver_index_completo);
      setPuedeGestionar(!!p?.puede_gestionar);
      setPuedeEditar(!!p?.puede_editar);
      setPuedeEliminar(!!p?.puede_eliminar);
      setCamposPorEtapa(parsePermisosCamposJson(p?.permisos_campos_json));
    } catch (e: any) {
      setError(e.message || "Error al cargar detalle.");
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const toggleCampo = (etapa: GestionEtapaKey, key: string, checked: boolean) => {
    setCamposPorEtapa((prev) => {
      const set = new Set(prev[etapa]);
      if (checked) set.add(key);
      else set.delete(key);
      return { ...prev, [etapa]: Array.from(set) };
    });
  };

  const seleccionarTodosEtapa = (etapa: GestionEtapaKey, marcar: boolean) => {
    const defs = PERMISOS_CAMPOS_POR_ETAPA[etapa];
    setCamposPorEtapa((prev) => ({
      ...prev,
      [etapa]: marcar ? defs.map((d) => d.key) : [],
    }));
  };

  const guardar = async () => {
    if (!detalle?.id) return;
    try {
      setGuardando(true);
      setError(null);
      setOkMsg(null);
      const permisos_campos_json = JSON.stringify(camposPorEtapa);
      const res = await fetch(`${baseUrl}/usuarios/${detalle.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({
          nombre: detalle.nombre,
          usuario: detalle.usuario,
          correo: detalle.correo || "",
          rol: detalle.rol?.nombre || "operacion",
          usuarioCreador: getUsuarioCreador(),
          puede_crear: puedeCrear,
          puede_ver_index_completo: puedeVerIndex,
          puede_gestionar: puedeGestionar,
          puede_editar: puedeEditar,
          puede_eliminar: puedeEliminar,
          permisos_campos_json,
        }),
      });
      if (!res.ok) throw new Error("No fue posible guardar los permisos.");
      setOkMsg("Permisos guardados correctamente.");
      await cargarLista();
    } catch (e: any) {
      setError(e.message || "Error al guardar.");
    } finally {
      setGuardando(false);
    }
  };

  const filtrados = React.useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter(
      (u) =>
        u.usuario.toLowerCase().includes(q) ||
        u.nombre.toLowerCase().includes(q) ||
        (u.rol?.nombre || "").toLowerCase().includes(q),
    );
  }, [usuarios, busqueda]);

  const camposEtapaLista = PERMISOS_CAMPOS_POR_ETAPA[etapaActual];
  const seleccionadosSet = new Set(camposPorEtapa[etapaActual]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Permisos de gestión de proyectos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Defina permisos globales y, por cada pestaña del flujo de gestión, qué elementos puede modificar el usuario.
        Si en una pestaña no marca ningún campo, no se aplicará permiso granular en esa etapa (listado vacío).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOkMsg(null)}>
          {okMsg}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography fontWeight={700}>Usuarios del módulo</Typography>
              <Button size="small" onClick={cargarLista} disabled={loadingLista}>
                Refrescar
              </Button>
            </Stack>
            <TextField
              size="small"
              fullWidth
              label="Buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ mb: 1 }}
            />
            {loadingLista ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2">Cargando…</Typography>
              </Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Rol</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtrados.map((u) => (
                    <TableRow
                      key={u.id}
                      hover
                      selected={selectedId === u.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelectedId(u.id);
                        setEtapaTab(0);
                        cargarDetalle(u.id);
                      }}
                    >
                      <TableCell>{u.usuario}</TableCell>
                      <TableCell>{u.nombre}</TableCell>
                      <TableCell>{u.rol?.nombre || "-"}</TableCell>
                    </TableRow>
                  ))}
                  {filtrados.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No hay usuarios.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {!selectedId && (
              <Typography color="text.secondary">Seleccione un usuario en la tabla.</Typography>
            )}
            {selectedId && loadingDetalle && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={22} />
                <Typography variant="body2">Cargando usuario…</Typography>
              </Stack>
            )}
            {selectedId && !loadingDetalle && detalle && (
              <>
                <Typography variant="subtitle1" fontWeight={800}>
                  {detalle.nombre}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({detalle.usuario})
                  </Typography>
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1.5 }}>
                  Rol en proyectos: {detalle.rol?.nombre || "-"}
                </Typography>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Permisos globales del módulo
                </Typography>
                <Stack spacing={0.25} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={puedeCrear} onChange={(e) => setPuedeCrear(e.target.checked)} />}
                    label="Puede crear proyectos"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={puedeVerIndex} onChange={(e) => setPuedeVerIndex(e.target.checked)} />
                    }
                    label="Puede ver listado completo"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={puedeGestionar} onChange={(e) => setPuedeGestionar(e.target.checked)} />
                    }
                    label="Puede gestionar"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={puedeEditar} onChange={(e) => setPuedeEditar(e.target.checked)} />}
                    label="Puede editar"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={puedeEliminar} onChange={(e) => setPuedeEliminar(e.target.checked)} />}
                    label="Puede eliminar"
                  />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Permisos por pestaña de gestión (campos)
                </Typography>
                <Tabs
                  value={etapaTab}
                  onChange={(_, v) => setEtapaTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
                >
                  {GESTION_ETAPA_KEYS.map((k, i) => (
                    <Tab key={k} label={GESTION_ETAPA_LABELS[k]} value={i} />
                  ))}
                </Tabs>

                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => seleccionarTodosEtapa(etapaActual, true)}>
                    Marcar todos ({GESTION_ETAPA_LABELS[etapaActual]})
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => seleccionarTodosEtapa(etapaActual, false)}>
                    Desmarcar todos
                  </Button>
                </Stack>

                <Grid container spacing={0.5}>
                  {camposEtapaLista.map(({ key, label }) => (
                    <Grid item xs={12} sm={6} key={`${etapaActual}-${key}`}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={seleccionadosSet.has(key)}
                            onChange={(e) => toggleCampo(etapaActual, key, e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">{label}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={guardar} disabled={guardando}>
                    {guardando ? "Guardando…" : "Guardar permisos"}
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
